"use client";

import { SnowpadLogo } from "@/components/logo";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { href: "/#features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/blog", label: "Blog" },
  { href: "/sign-in", label: "Sign In" },
] as const;

/**
 * Shared marketing site navigation.
 * Used on: /, /pricing, /blog, /blog/[slug].
 * "Features" always links back to /#features so it works from any page.
 * The active nav item is highlighted based on the current pathname.
 * On mobile, text links collapse into a hamburger dropdown; the CTA stays visible.
 */
export function SiteNav() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const linkClass = (href: string) => {
    const isActive = pathname === href || (href !== "/" && pathname.startsWith(href));
    return cn(
      "text-[10px] font-bold tracking-wide uppercase transition-colors rounded px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
    );
  };

  const ariaCurrentFor = (href: string) => {
    if (href === "/#features") return pathname === "/" ? ("location" as const) : undefined;
    if (href === "/blog") return pathname.startsWith("/blog") ? ("page" as const) : undefined;
    return pathname === href ? ("page" as const) : undefined;
  };

  return (
    <nav
      aria-label="Global"
      className="fixed top-0 left-0 right-0 z-50 border-b border-outline-variant/30 bg-surface/80 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto h-16 px-6 flex items-center justify-between">
        {/* Logo */}
        <Link
          href="/"
          aria-label="Snowpad Home"
          title="Snowpad Home"
          className="flex items-center gap-2 shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 p-1"
        >
          <SnowpadLogo size={32} />
          <span className="font-display font-bold text-xl tracking-tight text-foreground">Snowpad</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={linkClass(link.href)}
              aria-current={ariaCurrentFor(link.href)}
            >
              {link.label}
            </Link>
          ))}
          <Link href="/sign-up" className="btn-primary px-5 py-2 text-[10px] focus-visible:ring-offset-2">
            Get Started
          </Link>
        </div>

        {/* Mobile: CTA + hamburger */}
        <div className="flex items-center gap-3 md:hidden">
          <Link href="/sign-up" className="btn-primary px-4 py-2 text-[10px] focus-visible:ring-offset-2">
            Get Started
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden border-t border-outline-variant/30 bg-surface/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className={cn(linkClass(link.href), "block py-3 text-xs")}
                aria-current={ariaCurrentFor(link.href)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
